import { AnimatePresence, motion } from 'framer-motion';
import { ArrowDownToLine, ArrowUp, Infinity, Maximize2, Minimize2, MousePointerClick, Sun, ZoomIn, X } from 'lucide-react';

interface ChapterReaderControlsSheetProps {
    open: boolean;
    onClose: () => void;
    imageZoom: number;
    onImageZoomChange: (value: number) => void;
    imageBrightness: number;
    onImageBrightnessChange: (value: number) => void;
    infiniteScroll: boolean;
    onToggleInfiniteScroll: () => void;
    onScrollToTop: () => void;
    doubleTapControlsOnly: boolean;
    onToggleDoubleTapControlsOnly: () => void;
    onDownloadChapter: () => void;
    isDownloadingChapter: boolean;
    isFullscreen?: boolean;
    onToggleFullscreen?: () => void;
    showFullscreen?: boolean;
}

export default function ChapterReaderControlsSheet({
    open,
    onClose,
    imageZoom,
    onImageZoomChange,
    imageBrightness,
    onImageBrightnessChange,
    infiniteScroll,
    onToggleInfiniteScroll,
    onScrollToTop,
    doubleTapControlsOnly,
    onToggleDoubleTapControlsOnly,
    onDownloadChapter,
    isDownloadingChapter,
    isFullscreen = false,
    onToggleFullscreen,
    showFullscreen = false,
}: ChapterReaderControlsSheetProps) {
    return (
        <AnimatePresence>
            {open && (
                <>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[200] bg-black/60"
                        onClick={onClose}
                    />
                    <motion.div
                        initial={{ y: '100%' }}
                        animate={{ y: 0 }}
                        exit={{ y: '100%' }}
                        transition={{ type: 'spring', damping: 30, stiffness: 300 }}
                        className="fixed bottom-0 left-0 right-0 z-[201] w-full md:left-1/2 md:right-auto md:max-w-2xl md:-translate-x-1/2 bg-neutral-900 border-t border-white/10 md:border-x md:border-white/10 rounded-t-2xl max-h-[88vh] flex flex-col"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex justify-center pt-3 pb-2 shrink-0">
                            <div className="w-10 h-1 bg-white/20 rounded-full" />
                        </div>
                        <div className="flex items-center justify-between px-4 py-3 border-b border-white/5 shrink-0">
                            <h2 className="text-lg font-semibold text-white">Leitura</h2>
                            <button
                                type="button"
                                onClick={onClose}
                                className="p-2 rounded-lg hover:bg-white/5 text-white/50 hover:text-white"
                                aria-label="Fechar"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        <div className="overflow-y-auto px-4 py-4 pb-[max(1.25rem,env(safe-area-inset-bottom))] space-y-5">
                            <button
                                type="button"
                                onClick={() => {
                                    onScrollToTop();
                                    onClose();
                                }}
                                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left text-sm text-white/90 bg-white/5 hover:bg-white/10 active:bg-white/15 transition-colors"
                            >
                                <ArrowUp size={18} className="text-white/60 shrink-0" />
                                Ir ao topo
                            </button>

                            {showFullscreen && onToggleFullscreen && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        onToggleFullscreen();
                                        onClose();
                                    }}
                                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left text-sm transition-colors ${
                                        isFullscreen
                                            ? 'bg-orange-500/20 text-orange-300 border border-orange-500/35'
                                            : 'text-white/90 bg-white/5 hover:bg-white/10 border border-transparent'
                                    }`}
                                >
                                    {isFullscreen
                                        ? <Minimize2 size={18} className="text-orange-400 shrink-0" />
                                        : <Maximize2 size={18} className="text-white/60 shrink-0" />
                                    }
                                    Tela cheia {isFullscreen ? '(ligada)' : '(desligada)'}
                                </button>
                            )}

                            <button
                                type="button"
                                onClick={() => onToggleInfiniteScroll()}
                                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left text-sm transition-colors ${
                                    infiniteScroll
                                        ? 'bg-orange-500/20 text-orange-300 border border-orange-500/35'
                                        : 'text-white/90 bg-white/5 hover:bg-white/10 border border-transparent'
                                }`}
                            >
                                <Infinity size={18} className={`shrink-0 ${infiniteScroll ? 'text-orange-400' : 'text-white/60'}`} />
                                Leitura infinita {infiniteScroll ? '(ligada)' : '(desligada)'}
                            </button>

                            <button
                                type="button"
                                onClick={() => onToggleDoubleTapControlsOnly()}
                                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left text-sm transition-colors ${
                                    doubleTapControlsOnly
                                        ? 'bg-orange-500/20 text-orange-300 border border-orange-500/35'
                                        : 'text-white/90 bg-white/5 hover:bg-white/10 border border-transparent'
                                }`}
                            >
                                <MousePointerClick
                                    size={18}
                                    className={`shrink-0 ${doubleTapControlsOnly ? 'text-orange-400' : 'text-white/60'}`}
                                />
                                Mostrar controles apenas com duplo toque {doubleTapControlsOnly ? '(ligado)' : '(desligado)'}
                            </button>

                            <button
                                type="button"
                                disabled={isDownloadingChapter}
                                onClick={() => {
                                    onDownloadChapter();
                                    onClose();
                                }}
                                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left text-sm transition-colors ${
                                    isDownloadingChapter
                                        ? 'text-white/45 bg-white/5 cursor-wait'
                                        : 'text-white/90 bg-white/5 hover:bg-white/10 active:bg-white/15'
                                }`}
                            >
                                <ArrowDownToLine size={18} className="text-white/60 shrink-0" />
                                {isDownloadingChapter ? 'Preparando download...' : 'Baixar capítulo para offline'}
                            </button>

                            <div className="space-y-2">
                                <div className="flex items-center gap-2 text-white/80 text-sm">
                                    <ZoomIn size={16} className="text-white/50" />
                                    <span>Zoom</span>
                                    <span className="ml-auto font-mono text-orange-400/90">{imageZoom}%</span>
                                </div>
                                <input
                                    type="range"
                                    min={50}
                                    max={120}
                                    step={1}
                                    value={imageZoom}
                                    onChange={(e) => onImageZoomChange(Number(e.target.value))}
                                    className="w-full accent-orange-500 h-2"
                                />
                            </div>

                            <div className="space-y-2">
                                <div className="flex items-center gap-2 text-white/80 text-sm">
                                    <Sun size={16} className="text-white/50" />
                                    <span>Brilho</span>
                                    <span className="ml-auto font-mono text-orange-400/90">{imageBrightness}%</span>
                                </div>
                                <input
                                    type="range"
                                    min={40}
                                    max={140}
                                    step={1}
                                    value={imageBrightness}
                                    onChange={(e) => onImageBrightnessChange(Number(e.target.value))}
                                    className="w-full accent-orange-500 h-2"
                                />
                            </div>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
}
